import { useContext, useMemo } from 'react';
import {
  Divider,
  Box,
  Heading,
  HStack,
  Icon,
  VStack,
  Text,
  Skeleton,
  SkeletonText,
  Button,
} from '@chakra-ui/react';

import PageContainer from '~/containers/PageContainer';
import Content from '~/containers/Content';

import BackwardBar from '~/components/BackwardBar';

import authAtom from '~/recoil/atom/auth';
import { useRecoilValue } from 'recoil';

import { useNavigate, useParams } from 'react-router-dom';
import useCoupon from './hooks/useCoupon';

import { DisplayStoresContext } from '~/provider/DisplayStoresProvider';

import * as fp from 'lodash/fp';

import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import transformCouponDiscountString from '~/utils/transformCouponDiscountString';

import { AiOutlineShop } from 'react-icons/ai';
import { IoTime, IoStar, IoArrowForwardCircle } from 'react-icons/io5';
import { MdShoppingBasket } from 'react-icons/md';
import { FetchingDataStatus } from '~/types/utilities';

export default function CouponDetail() {
  const auth = useRecoilValue(authAtom);

  const { id = '', groupId = '' } = useParams();
  const navigate = useNavigate();

  const { stores } = useContext(DisplayStoresContext);

  const { coupon, couponFetchingStatus } = useCoupon(auth, id);

  const couponIsLoaded = !fp.eq(couponFetchingStatus, FetchingDataStatus.LOADING);

  const storeNameMap = useMemo(() => {
    return new Map(stores.map(({ name, objectID }) => [objectID, name]));
  }, [stores]);

  return (
    <PageContainer
      hideHeader
      hideNavBar
      afterHeaderComponent={
        <>
          <BackwardBar title="返回" onNavigate={() => navigate(-1)} />
        </>
      }
    >
      <Content>
        <Heading textAlign="center" size="md" borderBottomWidth="2px" borderColor="primary" py={4}>
          優惠卷詳情頁
        </Heading>
      </Content>
      <Box p={4}>
        <Content>
          <VStack align="stretch" spacing={6}>
            <Skeleton isLoaded={couponIsLoaded}>
              <Heading size="md">name</Heading>
            </Skeleton>
            <VStack align="stretch">
              <HStack>
                <Icon boxSize="1.25rem" color="gray.500" as={AiOutlineShop} />
                <Skeleton isLoaded={couponIsLoaded}>
                  <Text color="gray.500">
                    {coupon.stores
                      .map((id) => storeNameMap.get(id) || '')
                      .filter((name) => !!name)
                      .join('、')}
                  </Text>
                </Skeleton>
              </HStack>

              <HStack>
                <Icon boxSize="1.25rem" color="gray.500" as={IoTime} />
                <Skeleton isLoaded={couponIsLoaded}>
                  <Text color="gray.500">
                    {isValid(parseISO(coupon.endDate))
                      ? format(parseISO(coupon.endDate), 'yyyy/MM/dd')
                      : ''}
                  </Text>
                </Skeleton>
              </HStack>

              <HStack>
                <Icon boxSize="1.25rem" color="gray.500" as={IoStar} />
                <Skeleton isLoaded={couponIsLoaded}>
                  <Text color="danger.600">
                    {transformCouponDiscountString(coupon.type, coupon.condition)}
                  </Text>
                </Skeleton>
              </HStack>

              <HStack>
                <Icon boxSize="1.25rem" color="gray.500" as={MdShoppingBasket} />
                <Skeleton isLoaded={couponIsLoaded}>
                  <Text color="gray.500">不限取餐方式</Text>
                </Skeleton>
              </HStack>
            </VStack>

            <Divider />

            <Text color="gray.500">．優惠卷說明</Text>
            <SkeletonText noOfLines={4} skeletonHeight="0.8rem" isLoaded={couponIsLoaded}>
              <Text>{coupon.description}</Text>
            </SkeletonText>
            <Divider />
            <Text color="gray.500">．注意事項</Text>
            <SkeletonText noOfLines={3} skeletonHeight="0.8rem" isLoaded={couponIsLoaded}>
              <Text>{coupon.note}</Text>
            </SkeletonText>
            <Divider />
            <Button
              size="lg"
              alignSelf="center"
              w="15rem"
              rightIcon={<Icon as={IoArrowForwardCircle} boxSize="1.25rem" />}
              onClick={() => navigate(groupId ? `/group/${groupId}/filterMeal` : `/filterMeal`)}
            >
              點餐去
            </Button>
          </VStack>
        </Content>
      </Box>
    </PageContainer>
  );
}
